// src/components/SettingsPanel.tsx

import { useState, useEffect } from 'react'; // Import useEffect
import { settingsStore } from '../../scripts/settingsStore'; // Assuming correct path
import "../styles/settings.css"; // Ensure correct path

interface SettingsPanelProps {
  onClose: () => void
}

export const SettingsPanel = ({ onClose }: SettingsPanelProps) => {
  // Initialize state from the current store values
  const [settings, setSettings] = useState(settingsStore.values);
  const [visible, setVisible] = useState(true); // State to control panel visibility
  const [isSaving, setIsSaving] = useState(false); // State to show saving feedback


  // Subscribe to settingsStore changes
  useEffect(() => {
    const unsubscribe = settingsStore.onChange((updatedSettings: any) => {
      // Update component state when settings change in the store
      setSettings({ ...updatedSettings });
    });

    // Cleanup the subscription when the component unmounts
    return () => {
      unsubscribe();
    };
  }, []); // Empty dependency array means this effect runs once on mount and cleans up on unmount


  const save = async () => {
    setIsSaving(true); // Set saving state
    // Use the state directly, as it's kept in sync with the store via the effect
    try {
      await settingsStore.save(settings); // 💾 Save to Firestore!
      // Feedback can be a temporary message in the UI instead of an alert
      // console.log("Settings saved successfully!");
      // alert("Settings saved successfully!"); // Optional: keep alert for simplicity

    } catch (error) {
      console.error("Failed to save settings:", error);
      // alert("Failed to save settings. Please try again."); // Optional: keep alert
      // You might want to set an error state here to display message in UI
    } finally {
      setIsSaving(false); // Reset saving state
    }
  };

  const handleClose = () => {
    setVisible(false);
    onClose();
  };

  // Render null if not visible
  if (!visible) return null;

  return (
    <div className="settings-overlay">
      <div className="settings-panel">
        <button className="settings-close-button" onClick={handleClose} aria-label="Close settings">✖</button>

        <h2>⚙️ Settings</h2>

        {/* Gameplay Settings Section */}
        <div className="settings-section">
          <h3>Gameplay</h3>
          {/* Sensitivity Control */}
          <label htmlFor="sensitivity-slider">Sensitivity:</label>
          <span>{settings.sensitivity?.toFixed(2) ?? 'N/A'}</span>
          <input
            id="sensitivity-slider"
            type="range"
            min="1"
            max="100"
            step="0.1"
            value={settings.sensitivity ?? 50}
            onChange={e => setSettings(prev => ({ ...prev, sensitivity: parseFloat(e.target.value) }))}
          />

          {/* Invert Mouse Control */}
          <label htmlFor="invert-mouse-checkbox">Invert Mouse</label>
          <input
            id="invert-mouse-checkbox"
            type="checkbox"
            checked={settings.invertMouse ?? false}
            onChange={e => setSettings(prev => ({ ...prev, invertMouse: e.target.checked }))}
          />
        </div>


        {/* Sound Settings Section */}
        <div className="settings-section">
          <h3>Sound</h3>

          {/* Master Sound Switch */}
          <div className="setting-row">
            <label htmlFor="play-sound-checkbox">Master Audio</label>
            <input
              id="play-sound-checkbox"
              type="checkbox"
              checked={settings.play_sound ?? true}
              onChange={e => setSettings(prev => ({ ...prev, play_sound: e.target.checked }))}
            />
          </div>

          {/* Music Controls */}
          <div className="setting-group">
            <h4>Music</h4>
            <div className="setting-row">
              <label htmlFor="music-enabled-checkbox">Enabled</label>
              <input
                id="music-enabled-checkbox"
                type="checkbox"
                checked={settings.musicEnabled ?? true}
                onChange={e => setSettings(prev => ({ ...prev, musicEnabled: e.target.checked }))}
              />
            </div>
            <div className="setting-row">
              <label htmlFor="music-volume-slider">Volume: {((settings.musicVolume ?? 0.5) * 100).toFixed(0)}%</label>
              <input
                id="music-volume-slider"
                type="range"
                min="0"
                max="100" // UI scale 0-100
                step="10"
                value={(settings.musicVolume ?? 0.5) * 100}
                onChange={e => setSettings(prev => ({ ...prev, musicVolume: parseFloat(e.target.value) / 100 }))}
                disabled={!(settings.musicEnabled ?? true)}
              />
            </div>
          </div>

          {/* SFX Controls */}
          <div className="setting-group">
            <h4>Sound Effects</h4>
            <div className="setting-row">
              <label htmlFor="sfx-enabled-checkbox">Enabled</label>
              <input
                id="sfx-enabled-checkbox"
                type="checkbox"
                checked={settings.sfxEnabled ?? true}
                onChange={e => setSettings(prev => ({ ...prev, sfxEnabled: e.target.checked }))}
              />
            </div>
            <div className="setting-row">
              <label htmlFor="sfx-volume-slider">Volume: {((settings.sfxVolume ?? 0.5) * 100).toFixed(0)}%</label>
              <input
                id="sfx-volume-slider"
                type="range"
                min="0"
                max="100"
                step="10"
                value={(settings.sfxVolume ?? 0.5) * 100}
                onChange={e => setSettings(prev => ({ ...prev, sfxVolume: parseFloat(e.target.value) / 100 }))}
                disabled={!(settings.sfxEnabled ?? true)}
              />
            </div>
          </div>
        </div>


        {/* Buttons Container */}
        <div className="settings-buttons">
          <button className="save-button" onClick={save} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};
